import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Grid,
  useTheme,
  useMediaQuery,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
} from '@mui/material';

import {
  Search as SearchIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Person as PersonIcon,
  ReplySharp as ReplySharpIcon,
  PictureAsPdf as PdfIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import { getAllLeads } from '../../../controllers/crm/corretorControlles';
import { deleteLead } from '../../../controllers/crm/leadController';
import TransferirLead from './TransferirLead';

const CorretorLeads = ({ idconta, setSnackbar }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [transferDialog, setTransferDialog] = useState({
    open: false,
    lead: null
  });

  // Estados para dialog de exclusão
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    lead: null
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const maxLines = 300;

  const hoje = new Date();
  const seteDiasAtras = new Date();
  seteDiasAtras.setDate(hoje.getDate() - 7);

  const [filters, setFilters] = useState({
    datainicio: seteDiasAtras.toISOString().split('T')[0],
    datafim: hoje.toISOString().split('T')[0],
    corretor: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const buscarLeads = async () => {
    if (!filters.datainicio || !filters.datafim) {
      setError('Por favor, preencha as datas de início e fim');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const dados = {
        idconta,
        dataInicio: filters.datainicio,
        dataFim: filters.datafim,
        limite: maxLines,
        ...(filters.corretor && { corretor: filters.corretor })
      };

      const response = await getAllLeads(dados);

      if (response && response.data) {
        if (process.env.REACT_APP_SHOWLOG === 'true') console.log('Leads encontrados:', response.data);
        setLeads(response.data.data);
      } else {
        setError('Nenhum dado encontrado');
        setLeads([]);
      }
    } catch (err) {
      setError('Erro ao buscar leads');
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    const date = new Date(data);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedLeads = () => {
    if (!sortConfig.key) return leads;

    return [...leads].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'data_criacao') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleSortMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileSort = (key) => {
    handleSort(key);
    handleSortMenuClose();
  };

  const sortedLeads = getSortedLeads();

  const handleTransferClick = (lead) => {
    // console.log('Abrindo dialog de transferência para o lead:', lead);
    setTransferDialog({
      open: true,
      lead: lead
    });
  };

  const handleTransferClose = () => {
    setTransferDialog({
      open: false,
      lead: null
    });
  };

  const handleTransferSuccess = (leadAtualizado) => {
    setLeads(prevLeads =>
      prevLeads.map(lead =>
        lead.idlead === leadAtualizado.idlead ? leadAtualizado : lead
      )
    );
  };

  const formatarDataPDF = (data) => {
    if (!data) return '-';

    try {
      const date = new Date(data);

      if (isNaN(date.getTime())) {
        return '-';
      }

      const dataFormatada = date.toLocaleDateString('pt-BR');
      const horaFormatada = date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });

      return `${dataFormatada} ${horaFormatada}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '-';
    }
  };

  const gerarRelatorioPDF = () => {
    if (!leads || leads.length === 0) {
      setSnackbar({ severity: 'warning', message: 'Não há dados para gerar o relatório' });
      return;
    }

    try {
      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.text('Relatório de Leads dos Corretores', 14, 22);

      doc.setFontSize(12);
      const dataInicio = new Date(filters.datainicio).toLocaleDateString('pt-BR');
      const dataFim = new Date(filters.datafim).toLocaleDateString('pt-BR');
      doc.text(`Período: ${dataInicio} até ${dataFim}`, 14, 32);

      let currentY = 40;

      if (filters.corretor) {
        doc.text(`Filtro por corretor: ${filters.corretor}`, 14, currentY);
        currentY += 8;
      }

      const hoje = new Date().toLocaleDateString('pt-BR');
      const agora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      doc.text(`Gerado em: ${hoje} às ${agora}`, 14, currentY);
      currentY += 8;

      doc.text(`Total de leads: ${leads.length}`, 14, currentY);
      currentY += 8;

      const dadosTabela = sortedLeads.map(lead => [
        String(lead.corretor || 'Não atribuído'),
        String(lead.nome || '-'),
        String(lead.telefone || '-'),
        formatarDataPDF(lead.data_criacao),
        String(lead.motivo || '-'),
        String(lead.campanha || '-')
      ]);

      autoTable(doc, {
        head: [['Corretor', 'Lead', 'Telefone', 'Data', 'Situação', 'Campanha']],
        body: dadosTabela,
        startY: currentY + 5,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          halign: 'left'
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25 },
          3: { cellWidth: 30 },
          4: { cellWidth: 25 },
          5: { cellWidth: 35 }
        },
        margin: { top: 20, right: 14, bottom: 20, left: 14 },
        theme: 'striped',
        didDrawPage: function (data) {
          const pageCount = doc.internal.getNumberOfPages();
          const pageHeight = doc.internal.pageSize.height;
          doc.setFontSize(8);
          doc.text(`Página ${data.pageNumber} de ${pageCount}`, 14, pageHeight - 10);
        }
      });

      const nomeArquivo = `relatorio-leads-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nomeArquivo);

      setSnackbar({ severity: 'success', message: 'Relatório PDF gerado com sucesso!' });

    } catch (error) {
      console.error('Erro detalhado ao gerar PDF:', error);
      gerarRelatorioPDFSimples();
    }
  };

  const gerarRelatorioPDFSimples = () => {
    try {
      const doc = new jsPDF();

      let y = 20;

      doc.setFontSize(16);
      doc.text('Relatório de Leads dos Corretores', 20, y);
      y += 20;

      doc.setFontSize(10);
      const dataInicio = new Date(filters.datainicio).toLocaleDateString('pt-BR');
      const dataFim = new Date(filters.datafim).toLocaleDateString('pt-BR');
      doc.text(`Período: ${dataInicio} até ${dataFim}`, 20, y);
      y += 10;

      if (filters.corretor) {
        doc.text(`Filtro: ${filters.corretor}`, 20, y);
        y += 10;
      }

      doc.text(`Total: ${leads.length} leads`, 20, y);
      y += 20;

      doc.setFontSize(8);
      doc.text('Corretor', 20, y);
      doc.text('Lead', 60, y);
      doc.text('Telefone', 100, y);
      doc.text('Data', 130, y);
      doc.text('Situação', 160, y);
      y += 5;

      doc.line(20, y, 190, y);
      y += 5;

      sortedLeads.forEach((lead, index) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }

        doc.text(String(lead.corretor || '-').substring(0, 15), 20, y);
        doc.text(String(lead.nome || '-').substring(0, 15), 60, y);
        doc.text(String(lead.telefone || '-').substring(0, 15), 100, y);
        doc.text(formatarDataPDF(lead.data_criacao).substring(0, 15), 130, y);
        doc.text(String(lead.motivo || '-').substring(0, 10), 160, y);

        y += 5;
      });

      const nomeArquivo = `relatorio-leads-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nomeArquivo);

      setSnackbar({ severity: 'success', message: 'Relatório PDF gerado com sucesso (versão simples)!' });

    } catch (error) {
      console.error('Erro ao gerar PDF simples:', error);
      setSnackbar({ severity: 'error', message: 'Erro ao gerar PDF: ' + error.message });
    }
  };

  const renderMobileCard = (lead, index) => (
    <Paper
      key={index}
      elevation={2}
      sx={{
        p: 2,
        mb: 2,
        borderLeft: 4,
        borderLeftColor: 'primary.main'
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {lead.nome || 'Nome não informado'}
            </Typography>
            <Chip
              label={lead.motivo || 'Sem situação'}
              color={lead.motivo === 'Transferido' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">
              <strong>Corretor:</strong> {lead.corretor || 'Não atribuído'}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Telefone:</strong> {lead.telefone || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Data:</strong> {formatarData(lead.data_criacao)}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>Campanha:</strong> {lead.campanha || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="body2">
            <strong>META Formulário:</strong> {lead.idformulario || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {lead.idetapa === process.env.REACT_APP_ID_ETAPAMDESIGNADO && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<ReplySharpIcon />}
                onClick={() => handleTransferClick(lead)}
                size="small"
              >
                Transferir Lead
              </Button>
            )}

            {podeExcluir(lead) && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick(lead)}
                size="small"
              >
                Excluir Lead
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const podeExcluir = (lead) => {
    return lead.motivo?.toLowerCase() === 'aguardando';
  };

  const handleDeleteClick = (lead) => {
    setDeleteDialog({
      open: true,
      lead: lead
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false,
      lead: null
    });
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const dados = {
        idlead: deleteDialog.lead.idlead,
        idconta: idconta,
        version: deleteDialog.lead.version
      };

      const response = await deleteLead(dados);

      if (response.status !== 200) {
        setError('Erro ao excluir lead. Tente novamente.');
        setSnackbar({ severity: 'error', message: 'Erro ao excluir lead. Tente novamente.' });
        return;
      }

      setLeads(prevLeads => prevLeads.filter(lead => lead.idlead !== deleteDialog.lead.idlead));

      setSnackbar({ severity: 'success', message: 'Lead excluído com sucesso!' });
      handleDeleteClose();

    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      setError('Erro ao excluir lead. Tente novamente.');
      setSnackbar({ severity: 'error', message: 'Erro ao excluir lead. Tente novamente.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Leads dos Corretores
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Data Início"
              name="datainicio"
              value={filters.datainicio}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Data Fim"
              name="datafim"
              value={filters.datafim}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Nome do Corretor"
              name="corretor"
              value={filters.corretor}
              onChange={handleInputChange}
              placeholder="Digite o nome do corretor"
              size={isMobile ? "small" : "medium"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={buscarLeads}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              size={isMobile ? "small" : "medium"}
              sx={{ height: { xs: 40, md: 56 } }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Mensagens de erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabela/Cards de Leads */}
      {leads && leads.length > 0 ? (
        <>
          {/* Total de leads, ordenação e botão PDF */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" component="span">
                Total de leads:
              </Typography>
              <Typography variant="h5" component="span" color="primary" fontWeight="bold">
                {leads.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                (Max: {maxLines})
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Botão Gerar PDF */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={gerarRelatorioPDF}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? 'PDF' : 'Gerar PDF'}
              </Button>

              {/* Ordenação para mobile */}
              {isMobile && (
                <IconButton onClick={handleSortMenuClick}>
                  <SortIcon />
                </IconButton>
              )}
            </Box>

            {isMobile && (
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleSortMenuClose}
              >
                <MenuItem onClick={() => handleMobileSort('corretor')}>
                  <ListItemIcon>
                    {sortConfig.key === 'corretor' && sortConfig.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  </ListItemIcon>
                  <ListItemText>Ordenar por Corretor</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleMobileSort('nome')}>
                  <ListItemIcon>
                    {sortConfig.key === 'nome' && sortConfig.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  </ListItemIcon>
                  <ListItemText>Ordenar por Lead</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleMobileSort('data_criacao')}>
                  <ListItemIcon>
                    {sortConfig.key === 'data_criacao' && sortConfig.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  </ListItemIcon>
                  <ListItemText>Ordenar por Data</ListItemText>
                </MenuItem>
              </Menu>
            )}
          </Box>

          {isMobile ? (
            // Layout mobile - Cards
            <Box>
              {sortedLeads.map((lead, index) => renderMobileCard(lead, index))}
            </Box>
          ) : (
            // Layout desktop/tablet - Tabela
            <TableContainer component={Paper} elevation={2}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      <TableSortLabel>
                        Nr
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'corretor'}
                        direction={sortConfig.key === 'corretor' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('corretor')}
                      >
                        Corretor
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'nome'}
                        direction={sortConfig.key === 'nome' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('nome')}
                      >
                        Lead
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Telefone
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'data_criacao'}
                        direction={sortConfig.key === 'data_criacao' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('data_criacao')}
                      >
                        Data
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Situação
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Campanha
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      META Formulário
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', textAlign: 'center' }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedLeads.map((lead, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{lead.corretor || '-'}</TableCell>
                      <TableCell>{lead.nome || '-'}</TableCell>
                      <TableCell>{lead.telefone || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatarData(lead.data_criacao)}
                      </TableCell>
                      <TableCell>{lead.motivo || '-'}</TableCell>
                      <TableCell>{lead.campanha || '-'}</TableCell>
                      <TableCell>{lead.idformulario || '-'}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {lead.idetapa === process.env.REACT_APP_ID_ETAPAMDESIGNADO && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleTransferClick(lead)}
                              sx={{ minWidth: 'auto' }}
                            >
                              <ReplySharpIcon />
                            </IconButton>
                          )}

                          {podeExcluir(lead) && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(lead)}
                              sx={{ minWidth: 'auto' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}

                          {!lead.idetapa && !podeExcluir(lead) && (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <Paper
          elevation={1}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography color="text.secondary">
                Carregando leads...
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">
              {leads.length === 0 && !error ?
                'Utilize os filtros acima para buscar os leads' :
                'Nenhum lead encontrado'
              }
            </Typography>
          )}
        </Paper>
      )}

      {/* Componente de Transferência */}
      <TransferirLead
        open={transferDialog.open}
        onClose={handleTransferClose}
        lead={transferDialog.lead}
        idconta={idconta}
        onSuccess={handleTransferSuccess}
        setSnackbar={setSnackbar}
      />

      {/* Dialog de Exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Confirmar Exclusão
        </DialogTitle>

        <DialogContent>
          {deleteDialog.lead && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Esta ação não pode ser desfeita!
              </Alert>

              <Typography variant="body1" gutterBottom>
                Deseja realmente excluir o seguinte lead?
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2">
                  <strong>Nome:</strong> {deleteDialog.lead.nome || 'Nome não informado'}
                </Typography>
                <Typography variant="body2">
                  <strong>Telefone:</strong> {deleteDialog.lead.telefone || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Corretor:</strong> {deleteDialog.lead.corretor || 'Não atribuído'}
                </Typography>
                <Typography variant="body2">
                  <strong>Situação:</strong> {deleteDialog.lead.motivo || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Data:</strong> {formatarData(deleteDialog.lead.data_criacao)}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleDeleteClose}
            disabled={deleteLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Excluindo...' : 'Confirmar Exclusão'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CorretorLeads;